import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  getProductData,
  selectGetProductData,
} from "../slices/get-product-data.slice";
import { GetProductDataProductsModel } from "features/stock-ordering/core/domain/get-product-data.model";

// Import your new action and selector
import {
  postRating,
  PostRatingState,
  resetpostRating,
  selectPostRating,
  checkExistingRatings, // Add this import
} from "../slices/post-product-ratings.slice"; // Adjust path as needed
import { RatingParam } from "features/stock-ordering/core/stock-ordering.params";

// Define a type for the individual product rating state
interface ProductRatingEntry {
  rating: number;
  review: string;
  // Remove orderId from here since it's not needed for the local state
  // orderId is handled at the submission level, not per product
}

// Map: productId (string) -> ProductRatingEntry
interface IndividualProductRatingsState {
  [productId: string]: ProductRatingEntry;
}

// Back Button Component
const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 mb-6"
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 19l-7-7 7-7"
        />
      </svg>
      <span className="font-medium">Back to Order</span>
    </button>
  );
};

// Modified StarRating component to be controlled
interface StarRatingProps {
  currentRating: number;
  onRatingChange: (newRating: number) => void;
}

const StarRating: React.FC<StarRatingProps> = ({
  currentRating,
  onRatingChange,
}) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`text-3xl transition-colors duration-200 ${
            star <= (hoverRating || currentRating)
              ? "text-yellow-400"
              : "text-gray-300"
          } hover:text-yellow-400`}
          onClick={() => onRatingChange(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
        >
          ★
        </button>
      ))}
      <span className="ml-3 text-gray-600">
        {currentRating > 0 && (
          <span className="font-medium">{currentRating} out of 5 stars</span>
        )}
      </span>
    </div>
  );
};

// Component to display order details
const OrderDetailsCard: React.FC<{ product: any }> = ({ product }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "PHP",
    }).format(amount);
  };

  // Helper function to check if a field has meaningful data
  const hasValue = (value: any) => {
    return value !== undefined && value !== null && value !== "";
  };

  // Check if delivered quantity doesn't match committed quantity
  const isQuantityMismatch =
    hasValue(product.delivered_qty) &&
    hasValue(product.commited_qty) &&
    product.delivered_qty !== product.commited_qty;

  // Check which fields exist for this specific product
  const fields = [
    {
      condition: hasValue(product.order_qty),
      label: "Order Quantity",
      value: product.order_qty,
    },
    {
      condition: hasValue(product.commited_qty),
      label: "Committed Quantity",
      value: product.commited_qty,
    },
    {
      condition: hasValue(product.dispatch_qty),
      label: "Dispatched Quantity",
      value: product.dispatch_qty,
    },
    {
      condition: hasValue(product.delivered_qty),
      label: "Delivered Quantity",
      value: product.delivered_qty,
    },
    {
      condition: hasValue(product.total_cost),
      label: "Initial Order Subtotal",
      value: formatCurrency(product.total_cost),
      isCurrency: true,
    },
    {
      condition: hasValue(product.dispatch_subtotal),
      label: "Dispatch Subtotal",
      value: formatCurrency(product.dispatch_subtotal),
      isCurrency: true,
    },
    {
      condition: hasValue(product.receive_subtotal),
      label: "Delivered Subtotal",
      value: formatCurrency(product.receive_subtotal),
      isCurrency: true,
    },
  ];

  // Get only the fields that have data for this product
  const availableFields = fields.filter((field) => field.condition);

  // Don't render the card if no fields have data for this product
  if (availableFields.length === 0) {
    return null;
  }

  return (
    <div
      className={`rounded-lg p-4 mb-4 ${
        isQuantityMismatch
          ? "bg-yellow-100 border border-yellow-300"
          : "bg-gray-50"
      }`}
    >
      <h4 className="text-lg font-semibold text-gray-800 mb-3">
        Order Details
        {isQuantityMismatch && (
          <span className="ml-2 text-sm font-normal text-yellow-700">
            ⚠️ Quantity Mismatch
          </span>
        )}
      </h4>

      <div className="space-y-2 text-sm text-gray-700">
        {availableFields.map((field, index) => (
          <div key={index}>
            <span className="font-medium">{field.label}:</span>{" "}
            {field.isCurrency ? field.value : field.value}
          </div>
        ))}
      </div>
    </div>
  );
};

export function ProductRateForm() {
  const [individualProductRatings, setIndividualProductRatings] =
    useState<IndividualProductRatingsState>({});
  const [submitted, setSubmitted] = useState(false); // This will indicate local form submission success
  const [expandedProducts, setExpandedProducts] = useState<{
    [productId: string]: boolean;
  }>({}); // Track which products are expanded

  // Add access control state
  const [accessAllowed, setAccessAllowed] = useState<boolean | null>(null);

  const { orderId } = useParams<{ orderId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Selectors for product data and submission status
  const getProductDataState = useAppSelector(selectGetProductData);
  const postProductRatingsState = useAppSelector(selectPostRating); // Select the new state

  // Handle back button click
  const handleBackClick = () => {
    navigate(-1);
  };

  // Add this useEffect FIRST to check access before anything else
  useEffect(() => {
    if (orderId) {
      console.log(
        "🛡️ Checking if user can access rating page for order:",
        orderId
      );

      // Reset any previous rating state
      dispatch(resetpostRating());

      // Check for existing ratings
      dispatch(checkExistingRatings(orderId))
        .unwrap()
        .then((result) => {
          console.log("🔍 Access check result:", result);
          if (result.hasRatings) {
            console.log("🚫 Order already rated, denying access");
            alert(
              "This order has already been rated. You cannot rate it again."
            );
            navigate(-1);
            setAccessAllowed(false);
          } else {
            console.log("✅ Order not rated, allowing access");
            setAccessAllowed(true);
          }
        })
        .catch((error) => {
          console.error("Error checking rating access:", error);
          // On error, allow access (fail-safe) but log the error
          console.log("⚠️ Error checking access, allowing access as fail-safe");
          setAccessAllowed(true);
        });
    } else {
      // No orderId, deny access
      console.log("❌ No orderId provided, denying access");
      navigate(-1);
      setAccessAllowed(false);
    }
  }, [orderId, dispatch, navigate]);

  // Original useEffect for getting product data (only runs if access is allowed)
  useEffect(() => {
    if (orderId && accessAllowed === true) {
      console.log("📦 Loading product data for order:", orderId);
      dispatch(getProductData({ orderId: orderId }));
    }
  }, [dispatch, orderId, accessAllowed]);

  useEffect(() => {
    if (getProductDataState.data?.product_data && accessAllowed === true) {
      const initialRatings: IndividualProductRatingsState = {};
      getProductDataState.data.product_data.forEach((product) => {
        initialRatings[product.id] = { rating: 0, review: "" };
      });
      setIndividualProductRatings(initialRatings);
    }
  }, [getProductDataState.data?.product_data, accessAllowed]);

  // Use another useEffect to react to submission success/failure
  useEffect(() => {
    // Only process if access is allowed
    if (accessAllowed !== true) return;

    // Debug: Log the current Redux state
    console.log("Redux state change:", postProductRatingsState);

    if (postProductRatingsState.status === PostRatingState.success) {
      console.log("Submission successful!");
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        // Don't reset individualProductRatings here to avoid the error
        // setIndividualProductRatings({});
        dispatch(resetpostRating());
        // Navigate back to previous page
        navigate(-1);
      }, 3000);
    }
    if (postProductRatingsState.status === PostRatingState.fail) {
      console.log("Submission failed:", postProductRatingsState.message);
      alert(`Submission Error: ${postProductRatingsState.message}`); // Use message from state
      dispatch(resetpostRating());
    }
  }, [postProductRatingsState.status, dispatch, navigate, accessAllowed]);

  const handleProductRatingChange = useCallback(
    (productId: string, newRating: number) => {
      setIndividualProductRatings((prevRatings) => ({
        ...prevRatings,
        [productId]: { ...prevRatings[productId], rating: newRating },
      }));
    },
    []
  );

  const handleProductReviewChange = useCallback(
    (productId: string, newReview: string) => {
      setIndividualProductRatings((prevRatings) => ({
        ...prevRatings,
        [productId]: { ...prevRatings[productId], review: newReview },
      }));
    },
    []
  );

  const toggleProductDetails = useCallback((productId: string) => {
    setExpandedProducts((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    // Made async to await dispatch
    e.preventDefault();

    console.log("trigger");

    // Debug: Log the current state
    console.log("Product data:", getProductDataState.data?.product_data);
    console.log("Individual ratings:", individualProductRatings);

    // Prevent multiple submissions if already loading
    // if (postProductRatingsState.status === PostRatingState.inProgress) {
    //   return;
    // }

    const productsToReview = getProductDataState.data?.product_data || [];
    const reviewsToSubmit: RatingParam["productRating"] = [];

    const unratedProductNames: string[] = [];

    productsToReview.forEach((product) => {
      const productRating = individualProductRatings[product.id];

      if (!productRating || productRating.rating === 0) {
        unratedProductNames.push(product.product_name);
      } else {
        reviewsToSubmit.push({
          productId: product.product_id, // Make sure this matches your product data structure
          rating: productRating.rating,
          review: productRating.review || null, // Convert empty string to null to match interface
        });
      }
    });

    // Debug: Log what we're about to submit
    console.log("Reviews to submit:", reviewsToSubmit);

    if (unratedProductNames.length > 0) {
      const message = `Please provide a rating for the following product(s):\n\n- ${unratedProductNames.join(
        "\n- "
      )}`;
      alert(message);
      return;
    }

    // Debug: Log the dispatch payload
    const payload = {
      orderId,
      productRating: reviewsToSubmit,
    };
    console.log("Dispatching payload:", payload);

    // Dispatch the async thunk to submit ratings
    dispatch(postRating(payload));
  };

  // Show loading while checking access
  if (accessAllowed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Checking Access...
          </h2>
          <p className="text-gray-600">Verifying if you can rate this order.</p>
        </div>
      </div>
    );
  }

  // Don't render if access denied (backup - user should have been redirected)
  if (accessAllowed === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg text-center">
          <div className="text-red-500 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 mb-6">
            This order has already been rated. You cannot rate it again.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="bg-red-600 text-white py-2 px-6 rounded-md hover:bg-red-700 transition-colors duration-200"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Display loading state during submission
  if (PostRatingState.inProgress === postProductRatingsState.status) {
    return (
      <div className="max-w-2xl mx-auto p-8 bg-white rounded-lg shadow-lg text-center text-red-600">
        Submitting your reviews... Please wait.
      </div>
    );
  }

  // Display submitted success message
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-2xl mx-auto p-8 bg-white rounded-lg shadow-lg">
          <div className="text-center">
            <div className="text-green-500 text-6xl mb-4">✓</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Thank You!
            </h2>
            <p className="text-gray-600 mb-4">
              Your reviews have been submitted successfully.
            </p>
            <p className="text-sm text-gray-500">
              Redirecting back in a moment...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { data: productData } = getProductDataState;
  const productsToReview = productData?.product_data || [];

  // Safety check to prevent errors during state transitions
  if (!productsToReview || productsToReview.length === 0) {
    if (submitted) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-2xl mx-auto p-8 bg-white rounded-lg shadow-lg">
            <div className="text-center">
              <div className="text-green-500 text-6xl mb-4">✓</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Thank You!
              </h2>
              <p className="text-gray-600">
                Your reviews have been submitted successfully.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <BackButton onClick={handleBackClick} />
          <div className="p-8 bg-white rounded-lg shadow-lg">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Product Rating
              </h1>
              <p className="text-gray-600">
                Share your experience with each product in this order.
              </p>
            </div>
            <div className="mb-6 text-gray-600">
              No products found for this order to review.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <BackButton onClick={handleBackClick} />

        <div className="p-8 bg-white rounded-lg shadow-lg">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Product Rating
            </h1>
            <p className="text-gray-600">
              Share your experience with each product in this order.
            </p>
          </div>

          <div className="space-y-6">
            {orderId && (
              <div className="mb-4 p-4 border rounded-md bg-gray-50">
                <h2 className="text-xl font-semibold text-gray-700">
                  Order ID:{" "}
                  <span className="font-bold text-red-700">{orderId}</span>
                </h2>
              </div>
            )}

            {productsToReview && productsToReview.length > 0 ? (
              <form onSubmit={handleSubmit} className="space-y-8">
                {productsToReview.map((product) => {
                  // Additional safety check for product and rating data
                  if (!product || !product.id) return null;

                  const productRating = individualProductRatings?.[
                    product.id
                  ] || {
                    rating: 0,
                    review: "",
                  };
                  return (
                    <div
                      key={product.id}
                      className="p-5 border border-gray-200 rounded-lg shadow-sm bg-white"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-semibold text-gray-800">
                          {product.product_name}
                        </h3>
                        <button
                          type="button"
                          onClick={() => toggleProductDetails(product.id)}
                          className="flex items-center space-x-2 text-red-600 hover:text-red-800 transition-colors duration-200"
                        >
                          <span className="text-sm font-medium">
                            Order Details
                          </span>
                          <svg
                            className={`w-4 h-4 transition-transform duration-200 ${
                              expandedProducts[product.id] ? "rotate-180" : ""
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>

                      {/* Order Details Card - Only show if this specific product has order details AND is expanded */}
                      {expandedProducts[product.id] && (
                        <OrderDetailsCard product={product} />
                      )}

                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-3">
                          Overall Rating *
                        </label>
                        <StarRating
                          currentRating={productRating.rating}
                          onRatingChange={(newRating) =>
                            handleProductRatingChange(product.id, newRating)
                          }
                        />
                      </div>

                      <div className="mt-6">
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Your Review (Optional){" "}
                        </label>
                        <textarea
                          value={productRating.review}
                          onChange={(e) =>
                            handleProductReviewChange(
                              product.id,
                              e.target.value
                            )
                          }
                          placeholder={`Tell others about ${product.product_name}`}
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors resize-y"
                          maxLength={1000}
                        />
                        <div className="text-right text-sm text-gray-500 mt-1">
                          {productRating.review.length}/1000 characters
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="pt-4">
                  <div className="pt-4">
                    <button
                      type="submit"
                      className="w-full bg-red-600 text-white py-3 px-6 rounded-md hover:bg-red-700 focus:ring-4 focus:ring-red-200 transition-colors duration-200 font-medium text-lg"
                    >
                      Submit All Reviews
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <div className="mb-6 text-gray-600">
                No products found for this order to review.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
